<script setup lang="ts">
import { TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { onMounted, reactive, ref } from 'vue'

import {
  ACTION_LABELS,
  MODULE_LABELS,
  listOpLogs,
  type ActionKey,
  type ModuleKey,
  type OpLog,
} from '../api/oplogs'

const error = ref<unknown>(null)
const loading = ref(false)

const query = reactive({
  module: '' as ModuleKey | '',
  action: '' as ActionKey | '',
  keyword: '',
  dates: [] as string[],
  page: 1,
  size: 20,
})

const rows = ref<OpLog[]>([])
const total = ref(0)

async function load(): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listOpLogs({
      page: query.page,
      size: query.size,
      module: query.module || undefined,
      action: query.action || undefined,
      keyword: query.keyword.trim() || undefined,
      from: query.dates[0] || undefined,
      to: query.dates[1] || undefined,
    })
    rows.value = res.list
    total.value = res.total
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

function search(): void {
  query.page = 1
  void load()
}

onMounted(() => void load())
</script>

<template>
  <div class="yc-oplog">
    <header class="yc-oplog__bar">
      <el-select
        v-model="query.module"
        class="yc-oplog__module"
        placeholder="全部模块"
        clearable
        @change="search"
      >
        <el-option v-for="(text, value) in MODULE_LABELS" :key="value" :label="text" :value="value" />
      </el-select>
      <el-select
        v-model="query.action"
        class="yc-oplog__action"
        placeholder="全部动作"
        clearable
        @change="search"
      >
        <el-option v-for="(text, value) in ACTION_LABELS" :key="value" :label="text" :value="value" />
      </el-select>
      <el-input
        v-model="query.keyword"
        class="yc-oplog__keyword"
        placeholder="摘要/单据号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-date-picker
        v-model="query.dates"
        class="yc-oplog__dates"
        type="daterange"
        value-format="YYYY-MM-DD"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        @change="search"
      />
      <el-button type="primary" @click="search">查询</el-button>
      <el-button @click="load()">刷新</el-button>
    </header>

    <div v-if="error" class="yc-oplog__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading">
      <el-table :data="rows" row-key="id">
        <el-table-column label="时间" width="160">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="模块" width="90">
          <template #default="{ row }">{{ MODULE_LABELS[row.module as ModuleKey] ?? row.module }}</template>
        </el-table-column>
        <el-table-column label="动作" width="110">
          <template #default="{ row }">{{ ACTION_LABELS[row.action as ActionKey] ?? row.action }}</template>
        </el-table-column>
        <el-table-column prop="ref_no" label="单据号" width="160">
          <template #default="{ row }">{{ row.ref_no || '—' }}</template>
        </el-table-column>
        <el-table-column prop="summary" label="摘要" min-width="260" show-overflow-tooltip />
        <el-table-column prop="operator" label="操作人" width="110" />
      </el-table>
      <el-pagination
        v-if="total > query.size"
        class="yc-oplog__page"
        layout="total, prev, pager, next"
        :total="total"
        :page-size="query.size"
        :current-page="query.page"
        @current-change="(p: number) => ((query.page = p), load())"
      />
    </el-card>
  </div>
</template>

<style scoped lang="scss">
.yc-oplog {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__module,
  &__action {
    width: 130px;
  }

  &__keyword {
    width: 220px;
  }

  &__dates {
    width: 260px;
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
